// useItems — Phase 2 items v2 통합 TanStack Query 훅.
// queryKey 규칙:
//   ['items', 'list', filter]   — 목록 조회
//   ['items', 'detail', id]     — 단건 조회
//   ['items']                   — 뮤테이션 후 광역 무효화용
// U53 — deleteItem / removeItemTag / updateItem(group_id) 에 Undo 패턴 적용.
// useUndoContext가 있을 때만 undo를 등록. 없어도 기존 기능은 동일하게 동작.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import {
  itemsApi,
  type CreateItemInput,
  type ItemDetailResponse,
  type ListItemsFilter,
  type UpdateItemInput,
} from '../api/items';
import { UndoContext } from './useUndoContext';

// ─── queryKey 상수 ───────────────────────────────────────────

export const ITEMS_KEY = ['items'] as const;
export const ITEMS_LIST_KEY = (filter: ListItemsFilter) => ['items', 'list', filter] as const;
export const ITEMS_DETAIL_KEY = (id: number) => ['items', 'detail', id] as const;

// ─── 조회 훅 ─────────────────────────────────────────────────

/**
 * items 목록 조회 훅 — v2 통합.
 * q·type·group_id·group_scope·tag·pinned·sort·limit·offset 필터 지원.
 * group_scope='descendants' → group_id 및 모든 자손 그룹의 items 포함.
 */
export function useItems(filter: ListItemsFilter = {}) {
  return useQuery({
    queryKey: ITEMS_LIST_KEY(filter),
    queryFn: () => itemsApi.listItems(filter),
  });
}

/**
 * item 단건 조회 훅 (payload 복호화 포함).
 * id=null이면 비활성.
 */
export function useItem(id: number | null) {
  return useQuery({
    queryKey: id !== null ? ITEMS_DETAIL_KEY(id) : ['items', 'detail', null],
    queryFn: () => itemsApi.getItem(id!),
    enabled: id !== null,
  });
}

// ─── 뮤테이션 훅 ─────────────────────────────────────────────

/**
 * item 뮤테이션 훅 모음.
 * createItem / updateItem / deleteItem / touchItem / pinItem / unpinItem
 * addItemTag / removeItemTag 후 items 전체 캐시 무효화.
 * U53 — deleteItem / removeItemTag / updateItem(group_id) 에 Undo 패턴 적용.
 */
export function useItemMutations() {
  const qc = useQueryClient();
  // UndoContext — Provider 없으면 null. null이면 undo 미등록.
  const undoCtx = useContext(UndoContext);

  // items 목록 전체 무효화 헬퍼
  const invalidateItems = () => qc.invalidateQueries({ queryKey: ITEMS_KEY });

  // 단건 무효화 헬퍼
  const invalidateItem = (id: number) =>
    qc.invalidateQueries({ queryKey: ITEMS_DETAIL_KEY(id) });

  const createItem = useMutation({
    mutationFn: (body: CreateItemInput) => itemsApi.createItem(body),
    onSuccess: () => {
      invalidateItems();
    },
  });

  const updateItem = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: UpdateItemInput; prevGroupId?: number | null; itemName?: string }) =>
      itemsApi.updateItem(id, patch),
    // Optimistic update — 서버 응답 전 캐시를 즉시 갱신한다.
    onMutate: async ({ id, patch }) => {
      // 진행 중인 단건 리페치 취소
      await qc.cancelQueries({ queryKey: ITEMS_DETAIL_KEY(id) });
      // 이전 캐시 스냅샷 저장
      const previous = qc.getQueryData<ItemDetailResponse>(ITEMS_DETAIL_KEY(id));
      // payload 변경이 있을 때만 낙관적 갱신
      if (previous) {
        qc.setQueryData<ItemDetailResponse>(ITEMS_DETAIL_KEY(id), (old) => {
          if (!old) return old;
          return {
            ...old,
            item: {
              ...old.item,
              ...(patch.name !== undefined && { name: patch.name }),
              ...(patch.pinned !== undefined && { pinned: patch.pinned as 0 | 1 }),
              ...(patch.group_id !== undefined && { group_id: patch.group_id }),
            },
            // payload 부분 병합
            ...(patch.payload !== undefined && {
              payload: { ...old.payload, ...patch.payload },
              item: {
                ...old.item,
                ...(patch.name !== undefined && { name: patch.name }),
                payload: { ...old.item.payload, ...patch.payload },
              },
            }),
          };
        });
      }
      return { previous, id };
    },
    // 실패 시 롤백
    onError: (_err, _variables, context) => {
      if (context?.previous !== undefined) {
        qc.setQueryData(ITEMS_DETAIL_KEY(context.id), context.previous);
      }
    },
    onSuccess: (_data, variables) => {
      invalidateItems();
      invalidateItem(variables.id);
      // group_id 변경(이동)일 때만 Undo 등록
      if (undoCtx && variables.patch.group_id !== undefined && variables.prevGroupId !== undefined) {
        const label = variables.itemName
          ? `"${variables.itemName}" 이동됨`
          : '항목 이동됨';
        const prevGroupId = variables.prevGroupId;
        undoCtx.push({
          description: label,
          undo: async () => {
            await itemsApi.updateItem(variables.id, { group_id: prevGroupId });
            invalidateItems();
            invalidateItem(variables.id);
          },
        });
      }
    },
  });

  const deleteItem = useMutation({
    mutationFn: (args: { id: number; name?: string }) => itemsApi.deleteItem(args.id),
    onSuccess: (_data, args) => {
      invalidateItems();
      // Undo 등록: 삭제된 item 복원
      if (undoCtx) {
        const label = args.name ? `"${args.name}" 삭제됨` : '항목 삭제됨';
        undoCtx.push({
          description: label,
          undo: async () => {
            await itemsApi.restoreItem(args.id);
            invalidateItems();
          },
        });
      }
    },
  });

  const touchItem = useMutation({
    mutationFn: (id: number) => itemsApi.touchItem(id),
    onSuccess: (_data, id) => {
      // touch는 last_used_at/use_count만 변경 → 단건 캐시 + 목록 캐시 무효화
      invalidateItem(id);
      invalidateItems();
    },
  });

  const pinItem = useMutation({
    mutationFn: (id: number) => itemsApi.pinItem(id),
    onSuccess: (_data, id) => {
      invalidateItem(id);
      invalidateItems();
    },
  });

  const unpinItem = useMutation({
    mutationFn: (id: number) => itemsApi.unpinItem(id),
    onSuccess: (_data, id) => {
      invalidateItem(id);
      invalidateItems();
    },
  });

  const addItemTag = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => itemsApi.addItemTag(id, name),
    onSuccess: (_data, variables) => {
      invalidateItem(variables.id);
      invalidateItems();
      // 태그 자동완성 캐시도 무효화 — 신규 태그 생성 가능성 있음
      qc.invalidateQueries({ queryKey: ['tags'] });
    },
  });

  const removeItemTag = useMutation({
    mutationFn: ({ itemId, tagId }: { itemId: number; tagId: number; tagName?: string; itemName?: string }) =>
      itemsApi.removeItemTag(itemId, tagId),
    onSuccess: (_data, variables) => {
      invalidateItem(variables.itemId);
      invalidateItems();
      qc.invalidateQueries({ queryKey: ['tags'] });
      // 태그 제거 Undo 등록 — 태그명이 있을 때만 (tagName 전달 시)
      if (undoCtx && variables.tagName) {
        const label = variables.itemName
          ? `"${variables.itemName}"에서 태그 "${variables.tagName}" 제거됨`
          : `태그 "${variables.tagName}" 제거됨`;
        const { itemId, tagName } = variables;
        undoCtx.push({
          description: label,
          undo: async () => {
            await itemsApi.addItemTag(itemId, tagName);
            invalidateItem(itemId);
            invalidateItems();
            qc.invalidateQueries({ queryKey: ['tags'] });
          },
        });
      }
    },
  });

  return {
    createItem,
    updateItem,
    deleteItem,
    touchItem,
    pinItem,
    unpinItem,
    addItemTag,
    removeItemTag,
  };
}
